import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAvatarUrl } from '../utils/avatar';
import { motion } from 'framer-motion';
import {
  FaPlus,
  FaCompass, FaUserFriends, FaBolt,
  FaPen, FaFire, FaMagic, FaTrophy, FaArrowRight
} from 'react-icons/fa';
import api, { authAPI, postsAPI, bookmarksAPI, hashtagsAPI, usersAPI, scheduledAPI, draftsAPI, potdAPI, securityAPI } from '../utils/api';
import CreatePostModal from '../components/CreatePostModal';
import Sidebar from '../components/Sidebar';
import StoriesBar from '../components/StoriesBar';
import QuickPointBar from '../components/QuickPointBar';
import PostCard from '../components/dashboard/PostCard';
import RightSidebar from '../components/dashboard/RightSidebar';
import { toast } from 'sonner';

const StatChip = ({ label, value, icon: Icon, onClick }) => (
  <div
    onClick={onClick}
    className={`flex items-center space-x-1.5 px-3 py-1.5 bg-gray-100/80 rounded-full ${onClick ? 'cursor-pointer hover:bg-gray-200/80 transition-colors' : ''}`}
    data-testid={`stat-${label.toLowerCase()}`}
  >
    <Icon className="text-gray-400" size={11} />
    <span className="text-xs font-bold text-gray-800">{value}</span>
    <span className={`text-xs text-gray-400 ${label.includes('Point') ? 'font-arthaus' : ''}`}>{label}</span>
  </div>
);

import confetti from 'canvas-confetti';

// POTD Winner Trophy Badge - shows next to winner's name
const POTDTrophyBadge = ({ size = 12 }) => (
  <span className="inline-flex items-center" title="Point of the Day Winner">
    <FaTrophy className="text-amber-500 animate-pulse" size={size} />
  </span>
);

const POTDWinnerBanner = ({ winner }) => {
  const confettiFired = React.useRef(false);

  React.useEffect(() => {
    if (winner && !confettiFired.current) {
      confettiFired.current = true;
      // Fire confetti burst
      const fire = (opts) => confetti({ ...opts, origin: { y: 0.7 }, disableForReducedMotion: true });
      fire({ particleCount: 60, spread: 80, startVelocity: 40, colors: ['#f59e0b', '#eab308', '#fbbf24', '#d97706', '#fcd34d'] });
      setTimeout(() => fire({ particleCount: 40, spread: 100, startVelocity: 30, colors: ['#f59e0b', '#eab308', '#fbbf24'] }), 300);
    }
  }, [winner]);

  if (!winner) return null;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="relative bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 border-2 border-amber-300/60 rounded-2xl p-5 mb-5 overflow-hidden"
      data-testid="potd-winner-banner"
    >
      {/* Shimmer effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-200/20 to-transparent animate-pulse pointer-events-none" />
      <div className="relative flex items-start space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-200/50">
          <FaTrophy className="text-white" size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1.5">
            <span className="text-xs font-bold uppercase tracking-widest text-amber-600 font-arthaus">Point of the Day Winner</span>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-bold">{winner.votes} votes</span>
          </div>
          <p className="text-sm text-gray-900 font-medium line-clamp-2 leading-relaxed">{winner.content}</p>
          <div className="flex items-center space-x-2 mt-3">
            <Link to={`/profile/${winner.author_username}`}>
              <img src={getAvatarUrl(winner.author_avatar, winner.author_name)} alt="" className="w-7 h-7 rounded-full object-cover ring-2 ring-amber-300 hover:ring-amber-500 transition-all" />
            </Link>
            <Link to={`/profile/${winner.author_username}`} className="hover:underline">
              <span className="text-sm font-bold text-gray-900">{winner.author_name}</span>
            </Link>
            <POTDTrophyBadge />
            <span className="text-xs text-gray-500">@{winner.author_username}</span>
            {winner.author_potd_wins > 1 && (
              <span className="text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded-full font-bold">{winner.author_potd_wins}x winner</span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const FeedTabs = ({ activeTab, setActiveTab }) => (
  <div className="flex space-x-1 bg-gray-100 p-1 rounded-full w-fit" data-testid="feed-tabs">
    {[
      { key: 'foryou', label: 'For You', icon: FaMagic },
      { key: 'following', label: 'Following', icon: FaUserFriends },
      { key: 'trending', label: 'Trending', icon: FaBolt },
      { key: 'discover', label: 'Discover', icon: FaCompass },
    ].map(({ key, label, icon: Icon }) => (
      <button
        key={key}
        onClick={() => setActiveTab(key)}
        data-testid={`tab-${key}`}
        className={`flex items-center space-x-1.5 px-4 py-2 rounded-full font-medium transition-all text-sm ${
          activeTab === key
            ? key === 'trending'
              ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-sm'
              : 'bg-gray-900 text-white shadow-sm'
            : 'text-gray-500 hover:text-gray-700'
        }`}
      >
        <Icon size={12} />
        <span className="hidden sm:inline">{label}</span>
      </button>
    ))}
  </div>
);

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [stats, setStats] = useState({ posts: 0, followers: 0, following: 0, scheduled: 0, drafts: 0 });
  const [activeTab, setActiveTab] = useState('foryou');
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [bookmarkedPosts, setBookmarkedPosts] = useState(new Set());
  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [trendingTags, setTrendingTags] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [potdWinner, setPotdWinner] = useState(null);
  const [nominatedPosts, setNominatedPosts] = useState(new Set());
  const [followedUsers, setFollowedUsers] = useState(new Set());
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const navigate = useNavigate();

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 400) {
        loadMorePosts();
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [posts, loadingMore, hasMore, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadDashboard(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadFeed(); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh feed when user returns to the page/tab
  useEffect(() => {
    const handleFocus = () => { loadFeed(); };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDashboard = async () => {
    try {
      const userRes = await authAPI.getMe();
      setUser(userRes.data);
      // Fetch actual counts from profile endpoint
      const profileRes = await usersAPI.getProfile(userRes.data.id);
      setStats(prev => ({
        ...prev,
        posts: profileRes.data.posts_count || 0,
        followers: profileRes.data.followers_count || 0,
        following: profileRes.data.following_count || 0,
      }));
      Promise.all([
        scheduledAPI.getAll().catch(() => ({ data: [] })),
        draftsAPI.getAll().catch(() => ({ data: [] })),
        hashtagsAPI.getTrending(5).catch(() => ({ data: [] })),
        usersAPI.getSuggested().catch(() => ({ data: [] })),
        potdAPI.getWinnerToday().catch(() => ({ data: null })),
        usersAPI.getFollowing(userRes.data.id).catch(() => ({ data: [] })),
      ]).then(([schedRes, draftsRes, tagsRes, suggestedRes, potdRes, followingRes]) => {
        setStats(prev => ({ ...prev, scheduled: schedRes.data.length, drafts: draftsRes.data.length }));
        setTrendingTags(Array.isArray(tagsRes.data) ? tagsRes.data.slice(0, 5) : []);
        setSuggestedUsers(Array.isArray(suggestedRes.data) ? suggestedRes.data.slice(0, 3) : []);
        setPotdWinner(potdRes.data);
        const followingIds = new Set((followingRes.data || []).map(u => u.id));
        setFollowedUsers(followingIds);
      });
    } catch {
      handleLogout();
    }
  };

  const loadFeed = async () => {
    setLoading(true);
    setHasMore(true);
    try {
      let response;
      if (activeTab === 'trending') response = await postsAPI.getTrendingFeed();
      else if (activeTab === 'following') response = await postsAPI.getFollowingFeed();
      else if (activeTab === 'discover') response = await postsAPI.getDiscoverFeed();
      else response = await postsAPI.getForYouFeed();
      setPosts(response.data);
      if (response.data.length < 20) setHasMore(false);
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    } finally {
      setLoading(false);
    }
  };

  const loadMorePosts = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      let response;
      const skip = posts.length;
      if (activeTab === 'trending') response = await api.get(`/posts/feed/trending?skip=${skip}&limit=20`);
      else if (activeTab === 'following') response = await api.get(`/posts/following?skip=${skip}&limit=20`);
      else if (activeTab === 'discover') response = await api.get(`/posts/feed/discover?skip=${skip}&limit=20`);
      else response = await api.get(`/posts/feed/foryou?skip=${skip}&limit=20`);
      const newPosts = response.data;
      if (newPosts.length === 0) setHasMore(false);
      else setPosts(prev => [...prev, ...newPosts]);
    } catch (err) { console.error('Failed to load more posts', err); } finally {
      setLoadingMore(false);
    }
  };

  const handleLogout = async () => {
    try { await authAPI.logout(); } catch { /* logout best-effort */ }
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    navigate('/');
    window.location.reload();
  };

  const handleLike = async (postId) => {
    try {
      await postsAPI.like(postId);
      const isLiked = likedPosts.has(postId);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes_count: isLiked ? p.likes_count - 1 : p.likes_count + 1 } : p));
      setLikedPosts(prev => {
        const next = new Set(prev);
        isLiked ? next.delete(postId) : next.add(postId);
        return next;
      });
    } catch { toast.error('Failed to like'); }
  };

  const handleBookmark = async (postId) => {
    try {
      await bookmarksAPI.toggle(postId);
      const isBookmarked = bookmarkedPosts.has(postId);
      setBookmarkedPosts(prev => {
        const next = new Set(prev);
        isBookmarked ? next.delete(postId) : next.add(postId);
        return next;
      });
      toast.success(isBookmarked ? 'Removed from bookmarks' : 'Bookmarked!');
    } catch { toast.error('Failed to bookmark'); }
  };

  const handleRepost = async (postId) => {
    try {
      const response = await postsAPI.repost(postId);
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, shares_count: response.data.reposted ? p.shares_count + 1 : p.shares_count - 1 } : p));
      toast.success(response.data.reposted ? 'Reposted!' : 'Repost removed');
    } catch { toast.error('Failed to repost'); }
  };

  const handleNominate = async (postId) => {
    try {
      const res = await potdAPI.nominate(postId);
      setNominatedPosts(prev => new Set(prev).add(postId));
      toast.success(res.data.message);
    } catch (err) {
      // If already nominated, try voting instead
      if (err.response?.data?.detail?.includes('already nominated')) {
        try {
          const res = await potdAPI.vote(postId);
          setNominatedPosts(prev => new Set(prev).add(postId));
          toast.success(res.data.message);
        } catch (voteErr) {
          toast.error(voteErr.response?.data?.detail || 'Already voted');
        }
      } else {
        toast.error(err.response?.data?.detail || 'Failed to nominate');
      }
    }
  };

  const toggleComments = async (postId) => {
    if (expandedComments[postId]) {
      setExpandedComments(prev => ({ ...prev, [postId]: false }));
    } else {
      try {
        const response = await postsAPI.getComments(postId);
        setComments(prev => ({ ...prev, [postId]: response.data }));
        setExpandedComments(prev => ({ ...prev, [postId]: true }));
      } catch { toast.error('Failed to load comments'); }
    }
  };

  const handleAddComment = async (postId) => {
    const content = newComment[postId];
    if (!content?.trim()) return;
    try {
      const response = await postsAPI.addComment(postId, content);
      setComments(prev => ({ ...prev, [postId]: [...(prev[postId] || []), response.data] }));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments_count: p.comments_count + 1 } : p));
      setNewComment(prev => ({ ...prev, [postId]: '' }));
      toast.success('Comment added!');
    } catch { toast.error('Failed to add comment'); }
  };

  const handleFollow = async (userId) => {
    const isCurrentlyFollowed = followedUsers.has(userId);
    try {
      if (isCurrentlyFollowed) {
        await usersAPI.unfollow(userId);
        setFollowedUsers(prev => { const n = new Set(prev); n.delete(userId); return n; });
        toast.success('Unfollowed');
      } else {
        await usersAPI.follow(userId);
        setFollowedUsers(prev => new Set(prev).add(userId));
        setSuggestedUsers(prev => prev.filter(u => u.id !== userId));
        toast.success('Following!');
      }
    } catch { toast.error('Failed to update follow'); }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await postsAPI.delete(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch { toast.error('Failed to delete post'); }
  };

  const handleReportPost = async (targetId, targetType) => {
    const reason = window.prompt('Reason for report: spam, harassment, hate, violence, misinformation, or other');
    if (!reason) return;
    try {
      await securityAPI.report({ target_type: targetType, target_id: targetId, reason: reason.toLowerCase() });
      toast.success('Report submitted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to report'); }
  };

  const handleBlockUser = async (userId) => {
    if (!window.confirm('Block this user? They won\'t be able to see your content or message you.')) return;
    try {
      await securityAPI.blockUser(userId);
      setPosts(prev => prev.filter(p => p.user_id !== userId));
      toast.success('User blocked');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to block'); }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setEditContent(post.content || '');
  };

  const handleSaveEdit = async () => {
    if (!editingPost) return;
    try {
      await postsAPI.edit(editingPost.id, { content: editContent });
      setPosts(prev => prev.map(p => p.id === editingPost.id ? { ...p, content: editContent, is_edited: true } : p));
      setEditingPost(null);
      toast.success('Post updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <Sidebar />

        <main className="col-span-12 md:col-span-10 pb-20 md:pb-0" data-testid="dashboard-page">
          {/* Stories */}
          <StoriesBar />

          <div className="px-4 sm:px-6 lg:px-8 py-5 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="flex items-center space-x-3">
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                    <span className="text-gray-500 font-bold text-sm">{user?.full_name?.[0] || '?'}</span>
                  </div>
                )}
                <div>
                  <h1 className="text-lg font-bold text-gray-900" data-testid="dashboard-greeting">
                    {user?.full_name?.split(' ')[0] || 'Welcome'}
                  </h1>
                  <div className="flex items-center space-x-2 mt-0.5">
                    <StatChip label="Points" value={stats.posts} icon={FaPen} onClick={() => navigate('/profile?tab=posts')} />
                    <StatChip label="Followers" value={stats.followers} icon={FaUserFriends} onClick={() => navigate('/profile?tab=followers')} />
                    <StatChip label="Following" value={stats.following} icon={FaCompass} onClick={() => navigate('/profile?tab=following')} />
                  </div>
                </div>
              </div>
              <button
                data-testid="create-post-btn"
                onClick={() => setShowCreatePost(true)}
                className="bg-gray-900 text-white hover:bg-gray-800 font-semibold rounded-full px-5 py-2.5 transition-all hover:scale-[1.02] active:scale-95 flex items-center space-x-2 text-sm shadow-sm self-start sm:self-auto"
              >
                <FaPlus size={11} />
                <span className="font-arthaus">Create Point</span>
              </button>
            </div>

            {/* Feed Tabs + Content */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Main Feed */}
              <div className="lg:col-span-8">
                <QuickPointBar />
                <POTDWinnerBanner winner={potdWinner} />
                <div className="flex items-center justify-between mb-5">
                  <FeedTabs activeTab={activeTab} setActiveTab={setActiveTab} />
                  <Link to="/feed" className="text-xs text-gray-400 hover:text-[#4A90E2] transition-colors hidden sm:flex items-center space-x-1">
                    <span>Full Feed</span>
                    <FaArrowRight size={9} />
                  </Link>
                </div>

                {loading ? (
                  <div className="space-y-4" data-testid="feed-loading">
                    {[1, 2, 3].map(i => (
                      <div key={`skeleton-${i}`} className="bg-white rounded-2xl h-44 animate-pulse border border-gray-100" />
                    ))}
                  </div>
                ) : posts.length === 0 ? (
                  <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center" data-testid="empty-feed">
                    <FaCompass className="text-3xl text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium mb-1">
                      {activeTab === 'following' ? 'Your Following feed is empty' : activeTab === 'trending' ? 'Nothing trending yet' : activeTab === 'foryou' ? 'Not enough data for your personalized feed yet' : 'No points to discover yet'}
                    </p>
                    <p className="text-gray-400 text-sm mb-4">
                      {activeTab === 'following' ? 'Follow people to see their posts here' : activeTab === 'foryou' ? 'Like and interact with posts to personalize your feed' : 'Be the first to share something!'}
                    </p>
                    <button
                      onClick={() => activeTab === 'following' ? setActiveTab('discover') : setShowCreatePost(true)}
                      className="bg-gray-900 text-white font-semibold rounded-full px-5 py-2.5 text-sm hover:bg-gray-800 transition-all"
                    >
                      {activeTab === 'following' ? 'Discover People' : 'Create a Point'}
                    </button>
                  </div>
                ) : (
                  <>
                  <div className="space-y-4" data-testid="posts-container">
                    {posts.map(post => (
                      <PostCard
                        key={post.id}
                        post={post}
                        likedPosts={likedPosts}
                        bookmarkedPosts={bookmarkedPosts}
                        onLike={handleLike}
                        onBookmark={handleBookmark}
                        onRepost={handleRepost}
                        onToggleComments={toggleComments}
                        expandedComments={expandedComments}
                        comments={comments}
                        newComment={newComment}
                        onCommentChange={(id, val) => setNewComment(prev => ({ ...prev, [id]: val }))}
                        onAddComment={handleAddComment}
                        onNominate={handleNominate}
                        nominatedPosts={nominatedPosts}
                        currentUserId={user?.id}
                        followedUsers={followedUsers}
                        onFollow={handleFollow}
                        onDelete={handleDeletePost}
                        onReport={handleReportPost}
                        onBlock={handleBlockUser}
                        onEdit={handleEditPost}
                      />
                    ))}
                  </div>
                  {loadingMore && (
                    <div className="flex justify-center py-6">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#4A90E2]"></div>
                    </div>
                  )}
                  {!hasMore && posts.length > 0 && (
                    <p className="text-center text-xs text-gray-400 py-6">You&apos;ve seen it all</p>
                  )}
                  </>
                )}
              </div>

              {/* Right Sidebar */}
              <div className="lg:col-span-4">
                <RightSidebar
                  stats={stats}
                  trendingTags={trendingTags}
                  suggestedUsers={suggestedUsers}
                  onCreatePost={() => setShowCreatePost(true)}
                  onFollow={handleFollow}
                  potdWinner={potdWinner}
                />
              </div>
            </div>
          </div>
        </main>
      </div>

      {showCreatePost && (
        <CreatePostModal
          onClose={() => setShowCreatePost(false)}
          onPostCreated={() => { toast.success('Point created!'); loadFeed(); loadDashboard(); }}
        />
      )}

      {/* Edit Post Modal */}
      {editingPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6" data-testid="edit-post-modal">
            <h3 className="text-lg font-bold mb-4"><span className="font-arthaus">Edit Point</span></h3>
            <textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              className="w-full border border-gray-200 rounded-xl p-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A90E2] resize-none"
              rows={4}
              data-testid="edit-post-textarea"
            />
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setEditingPost(null)} className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleSaveEdit} data-testid="save-edit-btn" className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-700">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
